import { useEffect, useMemo, useState } from "react";
import { Activity, BellOff, BellRing, FileSearch, Globe2, LogOut, MessageSquarePlus, Search, Settings, ShieldCheck, Users } from "lucide-react";
import { searchIndexedMessages } from "../localMessages";
import type { Chat, Session } from "../types";
import { UserAvatar } from "./UserAvatar";

type Props = Readonly<{
  chats: Chat[];
  connected: boolean;
  dndEnabled: boolean;
  pushEnabled: boolean;
  onAdmin: () => void;
  onBrowseGroups: () => void;
  onCreate: () => void;
  onLogout: () => void;
  onManageDevices: () => void;
  onProfile: () => void;
  onEnablePush: () => void;
  onSelect: (chatId: string) => void;
  onToggleDnd: () => void;
  selectedId: string | null;
  session: Session;
}>;

export function chatTitle(chat: Chat, ownUserId: string): string {
  if (chat.kind === "group") return chat.name ?? "Группа";
  return chat.members.find((member) => member.id !== ownUserId)?.displayName ?? "Личный чат";
}

export function notificationUnreadCount(chat: Pick<Chat, "mentionCount" | "notificationLevel" | "unreadCount">): number {
  if (chat.notificationLevel === "muted") return 0;
  return chat.notificationLevel === "mentions" ? chat.mentionCount : chat.unreadCount;
}

export function totalUnread(chats: readonly Pick<Chat, "mentionCount" | "notificationLevel" | "unreadCount">[]): number {
  return chats.reduce((total, chat) => total + notificationUnreadCount(chat), 0);
}

export function Sidebar({ chats, connected, dndEnabled, pushEnabled, onAdmin, onBrowseGroups, onCreate, onLogout, onManageDevices, onProfile, onEnablePush, onSelect, onToggleDnd, selectedId, session }: Props) {
  const unreadTotal = totalUnread(chats);
  const [query, setQuery] = useState("");
  const [messageResults, setMessageResults] = useState<Array<{ chatId: string; createdAt: string; id: string; text: string }>>([]);
  const visibleChats = useMemo(() => {
    const value = query.trim().toLocaleLowerCase("ru-RU");
    if (!value) return chats;
    return chats.filter((chat) => chatTitle(chat, session.user.id).toLocaleLowerCase("ru-RU").includes(value));
  }, [chats, query, session.user.id]);
  useEffect(() => {
    const timer = window.setTimeout(() => void searchIndexedMessages(query).then(setMessageResults).catch(() => setMessageResults([])), 180);
    return () => window.clearTimeout(timer);
  }, [query]);
  const openResult = (chatId: string, messageId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("chat", chatId);
    url.searchParams.set("message", messageId);
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    onSelect(chatId);
  };
  return (
    <aside className={`sidebar ${selectedId ? "mobile-hidden" : ""}`}>
      <div className="sidebar-head">
        <div className="compact-brand"><img alt="" aria-hidden="true" src="/logo.png" /><b>JTUC Live</b></div>
        <span className="sidebar-head-actions"><button aria-label="Открытые группы" className="icon-button" onClick={onBrowseGroups} title="Открытые группы" type="button"><Globe2 /></button><button aria-label="Создать чат" className="icon-button accent" onClick={onCreate} title="Создать чат" type="button"><MessageSquarePlus /></button></span>
      </div>
      <div className="search-box"><Search size={18} /><input aria-label="Поиск по чатам и расшифрованным сообщениям" onChange={(event) => setQuery(event.target.value)} placeholder="Поиск на устройстве" value={query} /></div>
      <div className="sidebar-label">
        <span>Сообщения</span>
        {unreadTotal > 0 && <small aria-label={`Всего непрочитанных сообщений: ${unreadTotal}`}>{unreadTotal > 99 ? "99+" : unreadTotal}</small>}
      </div>
      <nav className="chat-list" aria-label="Список чатов">
        {query && messageResults.some((result) => chats.some((chat) => chat.id === result.chatId)) && <div className="local-search-results"><small><FileSearch />Сообщения на устройстве</small>{messageResults.filter((result) => chats.some((chat) => chat.id === result.chatId)).slice(0, 8).map((result) => { const resultChat = chats.find((chat) => chat.id === result.chatId)!; return <button key={result.id} onClick={() => openResult(result.chatId, result.id)} type="button"><b>{chatTitle(resultChat, session.user.id)}</b><span>{result.text}</span></button>; })}</div>}
        {visibleChats.length === 0 && (
          <div className="empty-list"><Users /><b>Пока нет чатов</b><span>Создайте личный диалог или защищённую группу.</span></div>
        )}
        {visibleChats.map((chat) => {
          const title = chatTitle(chat, session.user.id);
          const other = chat.members.find((member) => member.id !== session.user.id);
          const notifiedUnread = notificationUnreadCount(chat);
          return (
            <button className={`chat-row ${selectedId === chat.id ? "selected" : ""}`} key={chat.id} onClick={() => onSelect(chat.id)} type="button">
              {chat.kind === "group" ? <span className="avatar" style={{ background: chat.avatarColor }}>{chat.avatarUrl ? <img alt="" src={chat.avatarUrl} /> : <Users size={19} />}</span> : other ? <UserAvatar user={other} /> : <span className="avatar">?</span>}
              <span className="chat-row-copy">
                <span className="chat-row-title"><b>{title}</b>{chat.keyEnvelopes.length > 0 && <ShieldCheck size={14} aria-label="Зашифровано" />}</span>
                <small>{chat.lastMessage ? (chat.lastMessage.kind === "file" ? "Зашифрованный файл" : "Зашифрованное сообщение") : "Новый защищённый чат"}</small>
              </span>
              <span className="chat-row-meta">
                <time>{chat.lastMessage ? new Date(chat.lastMessage.receivedAt).toLocaleTimeString("ru", { hour: "2-digit", minute: "2-digit" }) : ""}</time>
                {chat.mentionCount > 0 && chat.notificationLevel !== "muted" && <b className="mention-badge" aria-label={`Непрочитанных упоминаний: ${chat.mentionCount}`}>@{chat.mentionCount > 99 ? "99+" : chat.mentionCount}</b>}
                {chat.notificationLevel === "all" && notifiedUnread > 0 && <b className="unread-badge" aria-label={`Непрочитанных сообщений: ${notifiedUnread}`}>{notifiedUnread > 99 ? "99+" : notifiedUnread}</b>}
              </span>
            </button>
          );
        })}
      </nav>
      <div className="sidebar-user">
        <button aria-label="Открыть свой профиль" className="sidebar-profile-button" onClick={onProfile} type="button"><UserAvatar className="avatar small" user={session.user} /><span><b>{session.user.displayName}</b><small><i className={connected ? "online" : "offline"} />{connected ? "в сети" : "подключение…"}</small></span></button>
        <span className="sidebar-user-actions">
          {session.user.isAdmin && <button aria-label="Состояние сервера" className="icon-button" onClick={onAdmin} title="Состояние сервера" type="button"><Activity /></button>}
          <button aria-label={pushEnabled ? "Push-уведомления включены" : "Включить Push-уведомления"} className={`icon-button ${pushEnabled ? "active" : ""}`} disabled={pushEnabled} onClick={onEnablePush} title={pushEnabled ? "Push-уведомления включены" : "Включить уведомления о сообщениях и звонках"} type="button"><BellRing /></button>
          <button aria-label="Не беспокоить" className={`icon-button ${dndEnabled ? "active" : ""}`} onClick={onToggleDnd} title={dndEnabled ? "Выключить «Не беспокоить»" : "Не беспокоить 8 часов"} type="button"><BellOff /></button>
          <button aria-label="Устройства и ключи" className="icon-button" onClick={onManageDevices} title="Устройства и ключи" type="button"><Settings /></button>
          <button aria-label="Выйти" className="icon-button" onClick={onLogout} title="Выйти" type="button"><LogOut /></button>
        </span>
      </div>
    </aside>
  );
}
